import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import {Box} from '@mui/material';
import {Body, Tooltip} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import Tag, {TagColor} from '@/components/tag/Tag';
import type {
  CampaignCreatorRow,
  CampaignDeliverables,
} from '../../types/creatorTabTypes';
import styles from './FulfillmentStatusCell.module.scss';

dayjs.extend(utc);

interface FulfillmentStatusCellProps {
  creator: CampaignCreatorRow;
  deliverableType: CampaignDeliverables;
}

function formatOptionalDate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  return dayjs.utc(value).format(MONTH_SHORT_DAY_YEAR_FORMAT);
}

function buildDeliveredTooltip(
  deliveredDate: string | null,
  courierMessage: string | null
): string {
  const lines = [
    deliveredDate
      ? translate('Delivered at: {date}', {date: deliveredDate})
      : translate('Delivered'),
    courierMessage
      ? translate('Courier message: {message}', {message: courierMessage})
      : null,
  ].filter(Boolean) as string[];

  return lines.join('\n');
}

function StatusContent({
  label,
  color,
  date,
  tooltip,
}: {
  label: string;
  color: TagColor;
  date?: string | null;
  tooltip?: string | null;
}) {
  const content = (
    <div className={styles.statusCell}>
      <Tag label={label} color={color} size="small" />
      {date ? (
        <Body size="sm" className={styles.date}>
          {date}
        </Body>
      ) : null}
    </div>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip title={tooltip} arrow placement="top" className={styles.tooltip}>
      <Box component="span" className={styles.tooltipTrigger}>
        {content}
      </Box>
    </Tooltip>
  );
}

function FulfillmentStatusCell({
  creator,
  deliverableType,
}: FulfillmentStatusCellProps) {
  const {fulfillmentState} = creator;

  if (!fulfillmentState) {
    return <Body size="md">-</Body>;
  }

  if (deliverableType === 'gift_codes') {
    const isDelivered = fulfillmentState === 'fulfilled';
    const deliveredDate = formatOptionalDate(creator.deliveredAt);

    return (
      <StatusContent
        label={isDelivered ? translate('Delivered') : translate('Pending')}
        color={isDelivered ? 'success' : 'neutral'}
        date={isDelivered ? deliveredDate : null}
        tooltip={
          isDelivered ? buildDeliveredTooltip(deliveredDate, null) : undefined
        }
      />
    );
  }

  if (fulfillmentState === 'awaiting_fulfillment') {
    return (
      <StatusContent
        label={translate('Awaiting fulfillment')}
        color="neutral"
      />
    );
  }

  if (fulfillmentState === 'fulfilled') {
    const deliveredDate = formatOptionalDate(creator.deliveredAt);

    return (
      <StatusContent
        label={translate('Delivered')}
        color="success"
        date={deliveredDate}
        tooltip={buildDeliveredTooltip(
          deliveredDate,
          creator.lastCheckpointMessage
        )}
      />
    );
  }

  const expectedDelivery = formatOptionalDate(creator.expectedDeliveryDate);
  const checkpointParts = [
    creator.lastCheckpointCity,
    creator.lastCheckpointState,
    creator.lastCheckpointZip,
  ].filter(Boolean);
  const tooltipLines = [
    creator.deliveryStatus ? creator.deliveryStatus : null,
    expectedDelivery
      ? translate('Delivery expected: {date}', {date: expectedDelivery})
      : null,
    checkpointParts.length > 0
      ? translate('Current location: {location}', {
          location: checkpointParts.join(' '),
        })
      : null,
    creator.lastCheckpointMessage
      ? translate('Courier message: {message}', {
          message: creator.lastCheckpointMessage,
        })
      : null,
  ].filter(Boolean);

  return (
    <StatusContent
      label={translate('In transit')}
      color="info"
      date={
        expectedDelivery
          ? translate('Expected {date}', {date: expectedDelivery})
          : null
      }
      tooltip={tooltipLines.length > 0 ? tooltipLines.join('\n') : undefined}
    />
  );
}

export default FulfillmentStatusCell;
